import React from 'react';
import { Button, Drawer, Tooltip, useToasts } from 'freemium-ui';
import { uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { DISABLE_EXPORT_DURATION_IN_MS } from 'routes/constants';
import {
  useGetCurrentAccountUser,
  useGetCurrentUser,
} from 'routes/screens/settings/hooks';
import {
  ColumnSettingsType,
  ConditionType,
  FormServFieldType,
  RuleType,
} from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import { COLUMNS_HIDE_ORDERING_KEY } from '../../../shared/constants';
import { useExportContacts } from '../../screens/contacts/hooks';
import { getSegmentPayloadFromFilters } from '../../utils';
import ExportColumnsForm from './ExportColumnsForm';

type ExportColumnsButtonProps = {
  fields?: FormServFieldType[];
  columnOrder?: string[];
  columnSettings?: ColumnSettingsType;
  hiddenColumns?: string[];
  filter: any;
  defaultFilterCondition?: ConditionType;
  rule?: RuleType;
  filterViewCount?: number;
};

let timeout;

const ExportColumnsButton = (props: ExportColumnsButtonProps) => {
  const {
    fields,
    columnSettings,
    columnOrder,
    defaultFilterCondition,
    hiddenColumns,
    filter,
    rule,
    filterViewCount,
  } = props;
  const [, setToast] = useToasts();
  const { t } = useTranslation();
  const [isExportDataDrawerOpen, toggleExportDataDrawer] =
    React.useState<boolean>(false);
  const [allFields, setAllFields] = React.useState<any>({});
  const [lastExported, setLastExported] = React.useState<string>();
  const { data: userInfo } = useGetCurrentUser({});
  const { data: accountInfo } = useGetCurrentAccountUser(
    userInfo?.display_id || '',
  );

  const { mutate: exportContacts, isLoading: isExportingContacts } =
    useExportContacts({
      setToast,
      callback: (isSuccess: boolean) => {
        if (isSuccess) {
          toggleExportDataDrawer(false);
          setLastExported(new Date().toISOString());
          timeout = setTimeout(() => {
            setLastExported('');
            clearTimeout(timeout);
            timeout = undefined;
          }, DISABLE_EXPORT_DURATION_IN_MS);
        }
      },
    });

  const onSubmit = ({
    localColumnOrder: lco,
    localHiddenColumns: lhc,
  }: {
    localColumnOrder: string[];
    localHiddenColumns: string[];
  }) => {
    const currentRule =
      rule || getSegmentPayloadFromFilters(filter, defaultFilterCondition);
    if (
      !currentRule ||
      !currentRule.condition_sets ||
      currentRule.condition_sets.length === 0
    ) {
      setToast({
        type: 'warning',
        delay: 3000,
        text: `${t('toast.warning.no_data')}`,
      });
      return;
    }
    const fieldsToExport = uniq(
      lco
        .filter((localColumnId) => !lhc.includes(localColumnId))
        .map((localColumnId) => allFields[localColumnId]?.name),
    );
    exportContacts({ field_names: fieldsToExport, rule: currentRule });
  };

  React.useEffect(() => {
    if (!fields || fields.length === 0) return;
    const newAllFields = {};
    fields.forEach((field) => {
      newAllFields[field[COLUMNS_HIDE_ORDERING_KEY]] = field;
    });
    setAllFields(newAllFields);
  }, [fields]);

  React.useEffect(() => {
    if (accountInfo) {
      const contact_last_exported_at =
        accountInfo?.meta?.contacts_last_exported_at || '0';

      const lastExportedTime: Date = lastExported
        ? new Date(lastExported || '0')
        : new Date(contact_last_exported_at || '0');

      const current_time: Date = new Date();
      const diff = current_time.valueOf() - lastExportedTime.valueOf();
      if (diff < DISABLE_EXPORT_DURATION_IN_MS) {
        setLastExported(lastExportedTime.toISOString());
        if (!timeout) {
          timeout = setTimeout(() => {
            setLastExported('');
            clearTimeout(timeout);
            timeout = undefined;
          }, DISABLE_EXPORT_DURATION_IN_MS - diff + 1000);
        }
      }
    }
  }, [accountInfo]);

  React.useEffect(() => {
    return () => {
      clearTimeout(timeout);
      timeout = undefined;
    };
  }, []);

  const isExportDisabled = React.useMemo(
    () =>
      new Date().valueOf() - new Date(lastExported || '0').valueOf() <
      DISABLE_EXPORT_DURATION_IN_MS,
    [lastExported, timeout],
  );
  const noContacts = filterViewCount === 0;

  return (
    <>
      <Tooltip
        label={
          isExportDisabled
            ? `${t('tooltip.export_disable')}`
            : noContacts
            ? `${t('tooltip.no_contact_to_export')}`
            : `${t('export')}`
        }
        position="bottom"
      >
        <Button
          type="secondary"
          inline
          onClick={() => {
            toggleExportDataDrawer(true);
          }}
          disabled={isExportDisabled || noContacts}
        >
          <Icon width="14" height="14" glyph="export" fill="none" />
        </Button>
      </Tooltip>
      <Drawer
        isOpen={isExportDataDrawerOpen}
        onDismiss={() => toggleExportDataDrawer(false)}
        isFullHeight
        size="medium"
        clickOutToClose={false}
        placement="right"
      >
        <ExportColumnsForm
          fields={fields}
          columnOrder={columnOrder}
          columnSettings={columnSettings}
          name="Export contacts"
          onSubmit={onSubmit}
          isExportingContacts={isExportingContacts}
          isFetching={isExportingContacts}
          hiddenColumns={hiddenColumns}
          toggleDrawer={(flag: boolean) => toggleExportDataDrawer(flag)}
        />
      </Drawer>
    </>
  );
};

export default ExportColumnsButton;
